import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useApiClient } from '../../hooks/useApiClient';
import './RemediationScriptViewer.css';

// ============================================================================
// TYPES
// ============================================================================

interface RemediationSection {
  id: string;
  title: string;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  sourceRecommendation: {
    text: string;
    station: string;
    category: string;
    findingId: string;
    severity: string;
  };
  rationale: string;
  code: string;
}

interface StructuredRemediationResult {
  metadata: {
    generatedAt: string;
    journeyId: string;
    problemType: string;
    remediationCategories: string[];
    aiModel: string;
    totalRecommendations: number;
    sectionCounts: { high: number; medium: number; low: number };
  };
  preamble: string;
  sections: RemediationSection[];
  postamble: string;
}

interface DatabricksSourceInfo {
  connectionId: string;
  connectionName: string;
  catalog: string;
  schema: string;
  table: string;
  workspaceUrl: string;
}

export interface RemediationScriptViewerProps {
  isOpen: boolean;
  onClose: () => void;
  data: StructuredRemediationResult;
  journeyId: string;
  onRegenerate?: () => void;
  regenerating?: boolean;
  databricksSource?: DatabricksSourceInfo;
}

// ============================================================================
// HELPERS
// ============================================================================

function getConfidenceBadgeClass(confidence: 'HIGH' | 'MEDIUM' | 'LOW'): string {
  switch (confidence) {
    case 'HIGH': return 'rsv-badge-high';
    case 'MEDIUM': return 'rsv-badge-medium';
    case 'LOW': return 'rsv-badge-low';
  }
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function buildFileTimestamp(): string {
  const now = new Date();
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
    String(now.getHours()).padStart(2, '0'),
    String(now.getMinutes()).padStart(2, '0'),
  ].join('');
}

function wrapComment(text: string, prefix: string = '# '): string {
  const maxWidth = 76;
  const lines: string[] = [];
  const words = text.split(/\s+/);
  let current = '';

  for (const word of words) {
    if (current.length + word.length + 1 > maxWidth) {
      lines.push(prefix + current);
      current = word;
    } else {
      current = current ? current + ' ' + word : word;
    }
  }
  if (current) {
    lines.push(prefix + current);
  }
  return lines.join('\n');
}

function assembleScript(
  data: StructuredRemediationResult,
  enabledSections: Set<string>,
  includeAll: boolean,
): string {
  const lines: string[] = [];
  const meta = data.metadata;

  lines.push('# ' + '='.repeat(76));
  lines.push('# DATA Compass - Data Remediation Script');
  lines.push('# ' + '='.repeat(76));
  lines.push('#');
  lines.push(`# Journey ID:    ${meta.journeyId}`);
  lines.push(`# Problem Type:  ${meta.problemType}`);
  lines.push(`# Generated:     ${formatTimestamp(meta.generatedAt)}`);
  lines.push(`# AI Model:      ${meta.aiModel}`);
  lines.push('#');
  lines.push('# Confidence Breakdown:');
  lines.push(`#   HIGH:   ${meta.sectionCounts.high} section(s)`);
  lines.push(`#   MEDIUM: ${meta.sectionCounts.medium} section(s)`);
  lines.push(`#   LOW:    ${meta.sectionCounts.low} section(s)`);
  lines.push('#');

  if (!includeAll) {
    const enabledCount = enabledSections.size;
    const totalCount = data.sections.length;
    lines.push(`# User Selection: ${enabledCount} of ${totalCount} sections enabled`);
    lines.push('#');
  }

  if (meta.remediationCategories.length > 0) {
    lines.push(`# Categories: ${meta.remediationCategories.join(', ')}`);
    lines.push('#');
  }

  lines.push('# ' + '='.repeat(76));
  lines.push('');

  if (data.preamble.trim()) {
    lines.push(data.preamble);
    lines.push('');
  }

  for (const section of data.sections) {
    const isEnabled = includeAll || enabledSections.has(section.id);

    if (isEnabled) {
      lines.push('# ' + '-'.repeat(76));
      lines.push(`# Section: ${section.title}`);
      lines.push(`# Confidence: ${section.confidence}`);
      lines.push('#');
      lines.push('# Source Recommendation:');
      lines.push(wrapComment(section.sourceRecommendation.text, '#   '));
      lines.push(`#   Station: ${section.sourceRecommendation.station}`);
      lines.push(`#   Category: ${section.sourceRecommendation.category}`);
      lines.push(`#   Finding: ${section.sourceRecommendation.findingId}`);
      lines.push(`#   Severity: ${section.sourceRecommendation.severity}`);
      lines.push('# ' + '-'.repeat(76));
      lines.push('');
      lines.push(section.code);
      lines.push('');
    } else {
      lines.push(`# SKIPPED BY USER: ${section.title}`);
      lines.push('');
    }
  }

  if (data.postamble.trim()) {
    lines.push(data.postamble);
    lines.push('');
  } else {
    // Fallback postamble when AI response was truncated or postamble is empty
    lines.push('# ' + '='.repeat(76));
    lines.push('# POST-REMEDIATION SUMMARY');
    lines.push('# ' + '='.repeat(76));
    lines.push('');
    lines.push('print("\\n" + "=" * 80)');
    lines.push('print("REMEDIATION COMPLETE")');
    lines.push('print("=" * 80)');
    lines.push('print(f"Final shape: {df.shape[0]:,} rows x {df.shape[1]} columns")');
    lines.push('print(f"New null count: {df.isnull().sum().sum():,}")');
    lines.push('');
    lines.push('df.to_csv(OUTPUT_FILE, index=False)');
    lines.push('print(f"\\nRemediated data saved to {OUTPUT_FILE}")');
    lines.push('');
  }

  return lines.join('\n');
}

function triggerDownload(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/x-python;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface ToggleSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  sectionId: string;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ checked, onChange, label, sectionId }) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onChange(!checked);
    }
  };

  return (
    <div
      className={`rsv-toggle ${checked ? 'rsv-toggle-on' : 'rsv-toggle-off'}`}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      id={`rsv-toggle-${sectionId}`}
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onChange(!checked);
      }}
      onKeyDown={handleKeyDown}
    >
      <div className="rsv-toggle-track">
        <div className="rsv-toggle-thumb" />
      </div>
    </div>
  );
};

interface SectionCardProps {
  section: RemediationSection;
  isExpanded: boolean;
  isEnabled: boolean;
  onToggleExpand: () => void;
  onToggleEnable: (enabled: boolean) => void;
}

const SectionCard: React.FC<SectionCardProps> = ({
  section,
  isExpanded,
  isEnabled,
  onToggleExpand,
  onToggleEnable,
}) => {
  const badgeClass = getConfidenceBadgeClass(section.confidence);

  return (
    <div className={`rsv-section-card ${isEnabled ? '' : 'rsv-section-disabled'}`}>
      <div
        className="rsv-section-header"
        onClick={onToggleExpand}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-controls={`rsv-section-body-${section.id}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleExpand();
          }
        }}
      >
        <div className="rsv-section-header-left">
          <span className="rsv-expand-icon" aria-hidden="true">
            {isExpanded ? '\u25BE' : '\u25B8'}
          </span>
          <span className={`rsv-confidence-badge ${badgeClass}`}>
            {section.confidence}
          </span>
          <span className="rsv-section-title">{section.title}</span>
        </div>
        <ToggleSwitch
          checked={isEnabled}
          onChange={onToggleEnable}
          label={`Enable section: ${section.title}`}
          sectionId={section.id}
        />
      </div>

      <div
        id={`rsv-section-body-${section.id}`}
        className={`rsv-section-body ${isExpanded ? 'rsv-section-body-open' : 'rsv-section-body-closed'}`}
        aria-hidden={!isExpanded}
      >
        <div className="rsv-section-body-inner">
          <div className="rsv-source-block">
            <div className="rsv-source-label">Source Recommendation</div>
            <p className="rsv-source-text">{section.sourceRecommendation.text}</p>
            <div className="rsv-source-meta">
              <span className="rsv-meta-chip">{section.sourceRecommendation.station}</span>
              <span className="rsv-meta-chip">{section.sourceRecommendation.category}</span>
              <span className="rsv-meta-chip">{section.sourceRecommendation.findingId}</span>
              <span className={`rsv-meta-chip rsv-severity-${section.sourceRecommendation.severity.toLowerCase()}`}>
                {section.sourceRecommendation.severity}
              </span>
            </div>
          </div>

          <div className="rsv-rationale">
            <span className="rsv-rationale-label">Rationale:</span>{' '}
            {section.rationale}
          </div>

          <div className="rsv-code-wrapper">
            <pre className="rsv-code-block"><code>{section.code}</code></pre>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const RemediationScriptViewer: React.FC<RemediationScriptViewerProps> = ({
  isOpen,
  onClose,
  data,
  journeyId,
  onRegenerate,
  regenerating = false,
  databricksSource,
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [enabledSections, setEnabledSections] = useState<Set<string>>(new Set());
  const [showDatabricksModal, setShowDatabricksModal] = useState(false);
  const [databricksExporting, setDatabricksExporting] = useState(false);
  const [databricksResult, setDatabricksResult] = useState<{ notebookUrl: string; notebookPath: string } | null>(null);
  const [databricksError, setDatabricksError] = useState<string | null>(null);
  const [databricksWorkspacePath, setDatabricksWorkspacePath] = useState('');

  useEffect(() => {
    if (isOpen && data) {
      const expanded = new Set<string>();
      const enabled = new Set<string>();

      for (const section of data.sections) {
        if (section.confidence === 'HIGH') {
          expanded.add(section.id);
          enabled.add(section.id);
        } else if (section.confidence === 'MEDIUM') {
          enabled.add(section.id);
        }
      }

      setExpandedSections(expanded);
      setEnabledSections(enabled);
    }
  }, [isOpen, data]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  const toggleExpand = useCallback((sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  const toggleEnable = useCallback((sectionId: string, enabled: boolean) => {
    setEnabledSections((prev) => {
      const next = new Set(prev);
      if (enabled) {
        next.add(sectionId);
      } else {
        next.delete(sectionId);
      }
      return next;
    });
  }, []);

  const handleEnableAll = useCallback(() => {
    setEnabledSections(new Set(data.sections.map((s) => s.id)));
  }, [data]);

  const handleDisableAll = useCallback(() => {
    setEnabledSections(new Set());
  }, []);

  const enabledCount = enabledSections.size;
  const totalCount = data.sections.length;
  const downloadEnabled = useMemo(() => enabledCount > 0, [enabledCount]);

  const handleDownloadScript = useCallback(() => {
    if (enabledCount === 0) return;
    const script = assembleScript(data, enabledSections, false);
    const filename = `cv_remediation_${journeyId}_${buildFileTimestamp()}.py`;
    triggerDownload(script, filename);
  }, [data, enabledSections, journeyId, enabledCount]);

  const handleDownloadAll = useCallback(() => {
    const script = assembleScript(data, enabledSections, true);
    const filename = `cv_remediation_${journeyId}_${buildFileTimestamp()}.py`;
    triggerDownload(script, filename);
  }, [data, enabledSections, journeyId]);

  const { authFetch } = useApiClient();

  const handleOpenDatabricksModal = useCallback(() => {
    setDatabricksError(null);
    setDatabricksResult(null);
    setDatabricksWorkspacePath('');
    setShowDatabricksModal(true);
  }, []);

  const handleDatabricksExport = useCallback(async () => {
    if (!databricksSource || databricksExporting) return;

    setDatabricksExporting(true);
    setDatabricksError(null);
    setDatabricksResult(null);

    try {
      const body: Record<string, string> = {
        connectionId: databricksSource.connectionId,
        catalog: databricksSource.catalog,
        schema: databricksSource.schema,
        table: databricksSource.table,
      };
      if (databricksWorkspacePath.trim()) {
        body.workspacePath = databricksWorkspacePath.trim();
      }

      const response = await authFetch(
        `/api/contextual-validity/journeys/${journeyId}/remediation-to-databricks`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to export notebook to Databricks');
      }

      setDatabricksResult({
        notebookUrl: result.notebookUrl,
        notebookPath: result.notebookPath,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      setDatabricksError(message);
    } finally {
      setDatabricksExporting(false);
    }
  }, [databricksSource, databricksExporting, databricksWorkspacePath, journeyId, authFetch]);

  if (!isOpen) return null;

  const { metadata, sections } = data;

  return createPortal(
    <div
      className="rsv-modal-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="rsv-modal-title"
    >
      <div className="rsv-modal-container">
        {/* Header */}
        <div className="rsv-modal-header">
          <div className="rsv-header-text">
            <h2 id="rsv-modal-title" className="rsv-title">
              Remediation Script Review
            </h2>
            <div className="rsv-subtitle">
              <span>{metadata.problemType}</span>
              <span className="rsv-subtitle-sep">&middot;</span>
              <span>{journeyId.substring(0, 8)}...</span>
              <span className="rsv-subtitle-sep">&middot;</span>
              <span>{formatTimestamp(metadata.generatedAt)}</span>
            </div>
          </div>
          <button
            type="button"
            className="rsv-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            &times;
          </button>
        </div>

        {/* Summary Bar */}
        <div className="rsv-summary-bar">
          <div className="rsv-summary-left">
            <span className="rsv-summary-total">{totalCount} sections</span>
            <span className="rsv-confidence-badge rsv-badge-high">
              {metadata.sectionCounts.high} High
            </span>
            <span className="rsv-confidence-badge rsv-badge-medium">
              {metadata.sectionCounts.medium} Medium
            </span>
            <span className="rsv-confidence-badge rsv-badge-low">
              {metadata.sectionCounts.low} Low
            </span>
          </div>
          <div className="rsv-summary-right">
            <button type="button" className="rsv-text-btn" onClick={handleEnableAll}>
              Enable All
            </button>
            <button type="button" className="rsv-text-btn" onClick={handleDisableAll}>
              Disable All
            </button>
          </div>
        </div>

        {/* Section Cards */}
        <div className="rsv-modal-body">
          {sections.map((section) => (
            <SectionCard
              key={section.id}
              section={section}
              isExpanded={expandedSections.has(section.id)}
              isEnabled={enabledSections.has(section.id)}
              onToggleExpand={() => toggleExpand(section.id)}
              onToggleEnable={(enabled) => toggleEnable(section.id, enabled)}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="rsv-modal-footer">
          <div className="rsv-footer-left">
            <span className="rsv-enabled-count">
              {enabledCount} of {totalCount} sections enabled
            </span>
            {onRegenerate && (
              <button
                type="button"
                className="btn rsv-regenerate-btn"
                onClick={onRegenerate}
                disabled={regenerating}
              >
                {regenerating ? (
                  <>
                    <span className="spinner-sm" aria-hidden="true" />
                    {' '}Regenerating...
                  </>
                ) : (
                  <>&#8635; Regenerate</>
                )}
              </button>
            )}
          </div>
          <div className="rsv-footer-right">
            {databricksSource && (
              <button
                type="button"
                className="btn btn-secondary rsv-databricks-btn"
                onClick={handleOpenDatabricksModal}
                title={`Export as PySpark notebook to ${databricksSource.connectionName}`}
              >
                Export to Databricks
              </button>
            )}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleDownloadAll}
            >
              Download All
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleDownloadScript}
              disabled={!downloadEnabled}
            >
              Download Script
            </button>
          </div>
        </div>

        {/* Databricks Export Modal */}
        {showDatabricksModal && databricksSource && (
          <div
            className="rsv-databricks-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) setShowDatabricksModal(false); }}
          >
            <div className="rsv-databricks-dialog">
              <h3 className="rsv-databricks-title">Export as Databricks Notebook</h3>

              <div className="rsv-databricks-info">
                <div className="rsv-databricks-field">
                  <label>Connection</label>
                  <span>{databricksSource.connectionName}</span>
                </div>
                <div className="rsv-databricks-field">
                  <label>Source Table</label>
                  <span>{databricksSource.catalog}.{databricksSource.schema}.{databricksSource.table}</span>
                </div>
                <div className="rsv-databricks-field">
                  <label>Workspace Path (optional)</label>
                  <input
                    type="text"
                    className="rsv-databricks-input"
                    value={databricksWorkspacePath}
                    onChange={(e) => setDatabricksWorkspacePath(e.target.value)}
                    placeholder="/Shared/Compass/Remediation/ (default)"
                  />
                </div>
              </div>

              <p className="rsv-databricks-note">
                The notebook will use PySpark and operate directly on the Unity Catalog table.
                No data will be downloaded locally.
              </p>

              {databricksError && (
                <div className="rsv-databricks-error">{databricksError}</div>
              )}

              {databricksResult && (
                <div className="rsv-databricks-success">
                  <p>Notebook exported successfully!</p>
                  <p className="rsv-databricks-path">{databricksResult.notebookPath}</p>
                  <a
                    href={databricksResult.notebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary rsv-databricks-open-link"
                  >
                    Open in Databricks
                  </a>
                </div>
              )}

              <div className="rsv-databricks-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDatabricksModal(false)}
                >
                  {databricksResult ? 'Close' : 'Cancel'}
                </button>
                {!databricksResult && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleDatabricksExport}
                    disabled={databricksExporting}
                  >
                    {databricksExporting ? (
                      <>
                        <span className="spinner-sm" aria-hidden="true" />
                        {' '}Exporting...
                      </>
                    ) : (
                      'Export Notebook'
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default RemediationScriptViewer;
